<template>
  <div class="studio st-shell">
    <header class="studio__topbar">
      <div class="studio__brand">
        <SigdexMark class="studio__brand-mark" />
        <span class="studio__brand-name">Sigdex Studio</span>
      </div>
      <div class="studio__topbar-main">
        <StudioBar />
      </div>
    </header>

    <div class="studio__body">
      <nav class="studio__rail" aria-label="Editor surfaces">
        <div class="studio__rail-group">
          <StIconButton
            v-for="s in railTop"
            :key="s.id"
            class="studio__rail-btn"
            :icon="s.icon"
            :label="s.label"
            :active="s.id === activeId"
            on-shell
            tip
            tip-placement="right"
            :aria-label="s.label"
            :aria-current="s.id === activeId ? 'page' : undefined"
            @click="setActive(s.id)"
          />
        </div>
        <div class="studio__rail-group">
          <StIconButton
            v-for="s in railFooter"
            :key="s.id"
            class="studio__rail-btn"
            :icon="s.icon"
            :label="s.label"
            :active="s.id === activeId"
            on-shell
            tip
            tip-placement="right"
            :aria-label="s.label"
            :aria-current="s.id === activeId ? 'page' : undefined"
            @click="setActive(s.id)"
          />
        </div>
      </nav>

      <section class="studio__content">
        <BundleGate v-if="showBundleGate" />
        <component :is="activeSurface.component" v-else />
      </section>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import StudioBar from './StudioBar.vue';
import SigdexMark from './SigdexMark.vue';
import BundleGate from './BundleGate.vue';
import { StIconButton } from './ui/index.js';
import { useSurfaces } from '../surfaces/index.js';
import { useLibrary } from '../state/library.js';

const { surfaces, activeId, activeSurface, setActive } = useSurfaces();
const lib = useLibrary();

const railTop = computed(() => surfaces.filter((s) => !s.footer));
const railFooter = computed(() => surfaces.filter((s) => s.footer));

// Block editor surfaces until a bundle is active; standalone surfaces (Settings)
// stay reachable so users aren't locked out of account/app settings.
const showBundleGate = computed(() => !activeSurface.value.standalone && !lib.activeEntry.value);
</script>

<style scoped>
.studio {
  display: flex;
  flex-direction: column;
  height: 100vh;
  overflow: hidden;
  background: var(--st-shell-bg);
}

/* ---- top bar ---- */
.studio__topbar {
  display: flex;
  align-items: stretch;
  flex-shrink: 0;
  background: var(--st-shell-bg);
  border-bottom: 1px solid var(--st-shell-border);
}
.studio__brand {
  display: flex;
  align-items: center;
  gap: var(--st-space-2);
  padding: 0 var(--st-space-4);
  border-right: 1px solid var(--st-shell-border);
}
.studio__brand-mark {
  height: 1.6rem;
  width: auto;
  color: var(--st-accent-on-dark);
}
.studio__brand-name {
  font-family: var(--st-font-display);
  font-size: var(--st-text-md);
  font-weight: var(--st-weight-semibold);
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--st-shell-text);
  white-space: nowrap;
}
.studio__topbar-main {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
}

/* ---- body: rail + content ---- */
.studio__body {
  display: flex;
  flex: 1;
  overflow: hidden;
}
.studio__rail {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  flex-shrink: 0;
  width: 52px;
  padding: var(--st-space-2) 0;
  background: var(--st-shell-bg);
  border-right: 1px solid var(--st-shell-border);
}
.studio__rail-group {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--st-space-1);
}
.studio__rail-btn {
  position: relative;
  width: 38px;
  height: 38px;
  border-radius: var(--st-radius-md);
  font-size: var(--st-text-lg);
}

/* active accent indicator on the rail edge */
.studio__rail-btn.is-active::before {
  content: '';
  position: absolute;
  left: -8px;
  top: 50%;
  transform: translateY(-50%);
  width: 3px;
  height: 20px;
  border-radius: var(--st-radius-full);
  background: var(--st-accent-on-dark);
}

.studio__content {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  background: var(--st-canvas);
}
</style>
